import { memo, useCallback, useMemo, useRef, useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import { grey, red } from "@mui/material/colors";
import {
  PlanTemplatePublishApprovalData,
  ChatMessageWithParts,
  ContentType,
} from "src/views/assistant/types";
import {
  deliverToolOutcome,
  useDeliveryWatcher,
} from "src/reusable/Chat/ChatStreaming/delivery";
import { useChatStreamStore } from "src/reusable/Chat/ChatStreaming/hooks/useChatStream";
import { useChatContext } from "../../context/ChatContext";
import { resolveOutcome } from "./resolveOutcome";
import { useAddFromWorkspace } from "src/api/projectFolders/projectFolderService";
import type { FileUploadResponse } from "src/api/projectFolders/types";
import {
  useGetExternalAction,
  useRejectExternalAction,
  useRunExternalAction,
} from "src/views/assistant/services/AtomAssistActions";
import { captureEvent, AA_EVENTS } from "src/config/posthog";

interface ParsedPromptData {
  promptData: PlanTemplatePublishApprovalData | null;
  existingResponse: PlanTemplatePublishApprovalData["user_response"] | null;
}

function stripToolResponseTags(text: string): string {
  // Tolerate leading/trailing whitespace around the tags — streamed part
  // text is often newline-padded, and a strict ^-anchor would fail the
  // JSON parse and leave the approval UI unrendered.
  return text
    .replace(/^\s*<tool_response>\s*/, "")
    .replace(/\s*<\/tool_response>\s*$/, "");
}

function wrapWithToolResponseTags(
  newText: string,
  originalText: string,
): string {
  const hasOpenTag = originalText.includes("<tool_response>");
  const hasCloseTag = originalText.includes("</tool_response>");

  let result = newText;
  if (hasOpenTag) result = "<tool_response>" + result;
  if (hasCloseTag) result = result + "</tool_response>";
  return result;
}

/**
 * Parse publish-approval data from raw text.
 * Handles both direct format and nested tool_result format.
 */
function parsePromptData(text: string): ParsedPromptData {
  try {
    const jsonText = text.includes("<tool_response>")
      ? stripToolResponseTags(text)
      : text;
    const parsed = JSON.parse(jsonText);
    const userResponse = parsed.user_response || null;

    if (parsed.type === "plan_template_publish_approval") {
      return {
        promptData: parsed as PlanTemplatePublishApprovalData,
        existingResponse: userResponse,
      };
    }

    if (parsed.message_type === "tool_result" && parsed.result) {
      const innerResult =
        typeof parsed.result === "string"
          ? JSON.parse(parsed.result)
          : parsed.result;
      if (innerResult?.type === "plan_template_publish_approval") {
        return {
          promptData: innerResult as PlanTemplatePublishApprovalData,
          existingResponse: innerResult.user_response || userResponse,
        };
      }
    }

    return { promptData: null, existingResponse: null };
  } catch {
    // JSON parse failures are expected for incomplete data during streaming
    return { promptData: null, existingResponse: null };
  }
}

interface PlanTemplatePublishApprovalProps {
  /** Raw text containing JSON prompt data */
  text: string;
  /** Whether the chat is still streaming */
  isChatConnected: boolean;
  /** Message ID this prompt belongs to (for updating the dialogue) */
  messageId?: string;
  /** Index of this content part within the message */
  partIndex?: number;
}

/**
 * PlanTemplatePublishApproval Component
 *
 * In-chat confirmation card for agent-requested plan-template publications.
 * Publishing writes into a shared project folder (visible to everyone the
 * project is shared with), so the agent can only request it — the user
 * explicitly approves or declines here. Approval reuses the same promotion
 * mutation as the manual "Add to Project" button (`useAddFromWorkspace`),
 * so write-grant enforcement, deconfliction/overwrite, ingestion, and query
 * invalidation are all inherited.
 */
const PlanTemplatePublishApproval = memo(
  ({
    text,
    isChatConnected,
    messageId,
    partIndex,
  }: PlanTemplatePublishApprovalProps) => {
    const { dialogueId } = useChatContext();
    // Attach to the HOST chat's stream store without mutating it. Calling
    // useAtomAssistChat here would overwrite the surface's stream config
    // (product, view-context getters, handlers) with this card's minimal
    // one and break product chats — see useChatStreamStore.
    const streamStore = useChatStreamStore();
    const trackDelivery = useDeliveryWatcher();
    const addFromWorkspace = useAddFromWorkspace();
    const runAction = useRunExternalAction();
    const rejectAction = useRejectExternalAction();

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    // Local mirror of the response for instant UI; the durable source of
    // truth is the ExternalAction status (the backend re-canonicalizes
    // dialogue parts on persist, so part edits don't survive a reload).
    const [localResponse, setLocalResponse] = useState<
      PlanTemplatePublishApprovalData["user_response"] | null
    >(null);
    // Set once the promotion write has succeeded. If the follow-up
    // `/run` status call fails, the prompt stays pending so the user can
    // retry — this ref makes that retry skip the (non-idempotent) write.
    const publishedRef = useRef<FileUploadResponse | null>(null);

    const { promptData, existingResponse } = useMemo(
      () => parsePromptData(text),
      [text],
    );

    // Durable resolution state, survives refresh and other devices.
    const { data: action, isLoading: actionLoading } = useGetExternalAction(
      promptData?.action_id,
      { enabled: localResponse === null && existingResponse === null },
    );
    const actionResponse = useMemo<
      PlanTemplatePublishApprovalData["user_response"] | null
    >(() => {
      const status = action?.status?.toLowerCase?.();
      if (!status || status === "pending") return null;
      if (status === "rejected") {
        return { approved: false, submitted_at: action?.updated_date ?? "" };
      }
      // completed / approved (and failed placeholder runs — the write
      // itself happened before the status call) count as published.
      return { approved: true, submitted_at: action?.updated_date ?? "" };
    }, [action]);

    // Persist the user response + outcome into the dialogue part so the
    // model sees the result and the card renders resolved after reloads.
    const updateDialogueWithResponse = useCallback(
      (approved: boolean, publishResult?: unknown) => {
        if (
          messageId === undefined ||
          partIndex === undefined ||
          streamStore === null
        ) {
          console.warn(
            "[PlanTemplatePublishApproval] Cannot record response in dialogue",
            { messageId, partIndex, hasStore: streamStore !== null },
          );
          return;
        }

        streamStore
          .getState()
          .setDialogue((prevDialogue: ChatMessageWithParts[]) =>
            prevDialogue.map((msg) => {
              if (msg.id !== messageId) return msg;

              const updatedParts = msg.contentParts.map((part, idx) => {
                if (
                  idx !== partIndex ||
                  part.type !== ContentType.TOOL_RESPONSE
                ) {
                  return part;
                }

                try {
                  const jsonText = stripToolResponseTags(part.text);
                  const parsed = JSON.parse(jsonText);
                  const updatedContent = {
                    ...parsed,
                    user_response: {
                      approved,
                      submitted_at: new Date().toISOString(),
                    },
                    // The compact tool row's caption (deriveCompletionMessage
                    // reads status_summary) — a resolved approval rolls up
                    // like any other completed tool.
                    status_summary: approved
                      ? `Published to projects/${promptData?.project_path}/`
                      : "Declined",
                    ...(publishResult ? { publish_result: publishResult } : {}),
                  };
                  const newText = wrapWithToolResponseTags(
                    JSON.stringify(updatedContent),
                    part.text,
                  );
                  return { ...part, text: newText };
                } catch (e) {
                  console.error(
                    "[PlanTemplatePublishApproval] Failed to update part:",
                    e,
                  );
                  return part;
                }
              });

              return { ...msg, contentParts: updatedParts };
            }),
          );
      },
      [messageId, partIndex, streamStore, promptData?.project_path],
    );

    const handleApprove = useCallback(async () => {
      if (!promptData) return;

      setIsSubmitting(true);
      setError(null);

      try {
        // Same request the manual Add to Project button sends — the
        // backend enforces the write grant and runs ingestion. Skipped on
        // a retry after a failed status call (see publishedRef).
        let result = publishedRef.current;
        if (result === null) {
          result = await addFromWorkspace.mutateAsync({
            folderId: promptData.folder_id,
            data: {
              source: "workspace_file",
              workspace_path: promptData.file_path,
              overwrite: promptData.overwrite,
            },
          });
          publishedRef.current = result;
          captureEvent(AA_EVENTS.FOLDER_FILE_ADDED, {
            folder_id: promptData.folder_id,
            source: "workspace_file",
            deconflicted: result.deconflicted ?? false,
            plan_template_publish: true,
          });
        }
        // Conversation-facing outcome the model reads on continuation.
        const message =
          `Published template '${result.name}' to ` +
          `projects/${promptData.project_path}/ — now discoverable via ` +
          `Search Plans.` +
          (result.deconflicted && result.original_name
            ? ` A file named '${result.original_name}' already existed, so ` +
              `this was saved under a new name; re-publish with ` +
              `overwrite=true to replace it instead.`
            : "");
        // Durable state: mark the staged action completed (runs the
        // status-only placeholder; the write above is the real effect).
        // Awaited BEFORE resolving the UI: an action left pending re-renders
        // the Publish prompt after a reload, and approving it again would
        // re-run the promotion and land a "name (2)" copy.
        if (promptData.action_id) {
          try {
            await runAction.mutateAsync(promptData.action_id);
          } catch {
            setError(
              "Published, but recording the approval failed — click Publish " +
                "to retry (the file won't be re-published).",
            );
            return;
          }
        }
        setLocalResponse({
          approved: true,
          submitted_at: new Date().toISOString(),
        });
        updateDialogueWithResponse(true, { ...result, message });
        if (streamStore) {
          // No injection payload for publishes — the layer cascades straight
          // to patched-dialogue resume, then a visible user message.
          const result = await deliverToolOutcome(streamStore, {
            dialogueId,
            promptId: promptData.prompt_id,
            fallbackMessage: message,
            patched: true,
          });
          void trackDelivery(result).then((verdict) => {
            if (verdict === "undelivered") {
              // Approval is recorded; the model learns it on the next turn.
              console.error("[PlanTemplatePublishApproval:undelivered]", {
                dialogueId,
                promptId: promptData.prompt_id,
              });
            }
          });
        }
      } catch (err: unknown) {
        const detail = (err as any)?.response?.data?.detail;
        setError(
          typeof detail === "string"
            ? detail
            : err instanceof Error
              ? err.message
              : "Publish failed",
        );
      } finally {
        // The resolved render doesn't read isSubmitting, but don't leave the
        // component in a perpetual submitting state.
        setIsSubmitting(false);
      }
    }, [
      promptData,
      addFromWorkspace,
      runAction,
      updateDialogueWithResponse,
      streamStore,
      trackDelivery,
      dialogueId,
    ]);

    const handleReject = useCallback(async () => {
      if (!promptData) return;
      const message = "The user declined publishing the template.";
      // Durable state: record the decline on the staged action. Awaited
      // BEFORE resolving the UI — an action left pending would re-render
      // the prompt after a reload as if never answered. On failure the
      // prompt stays pending; re-clicking Decline is writeless and safe.
      if (promptData.action_id) {
        try {
          await rejectAction.mutateAsync(promptData.action_id);
        } catch {
          setError("Failed to record the decline — please try again.");
          return;
        }
      }
      setError(null);
      setLocalResponse({
        approved: false,
        submitted_at: new Date().toISOString(),
      });
      updateDialogueWithResponse(false, { message });
      try {
        if (streamStore) {
          const result = await deliverToolOutcome(streamStore, {
            dialogueId,
            promptId: promptData.prompt_id,
            fallbackMessage: message,
            patched: true,
          });
          void trackDelivery(result).then((verdict) => {
            if (verdict === "undelivered") {
              console.error("[PlanTemplatePublishApproval:undelivered]", {
                dialogueId,
                promptId: promptData.prompt_id,
              });
            }
          });
        }
      } catch (err) {
        // The decline itself is already recorded (local + durable action);
        // a failed continuation just means the model learns it on the next
        // turn instead — don't let this become an unhandled rejection.
        console.error(
          "[PlanTemplatePublishApproval] Failed to continue after decline:",
          err,
        );
      }
    }, [
      promptData,
      rejectAction,
      updateDialogueWithResponse,
      streamStore,
      trackDelivery,
      dialogueId,
    ]);

    // Check if content is complete (has closing tag, raw JSON, or stream ended)
    const isContentComplete = useMemo(() => {
      const normalizedText = text.trimStart();
      if (normalizedText.includes("</tool_response>")) return true;
      if (!normalizedText.startsWith("<tool_response>")) return true;
      return !isChatConnected;
    }, [text, isChatConnected]);

    if (!promptData || !isContentComplete) {
      return null;
    }

    // Resolved — same ⎿ summary style as rolled-up tool rows. Sources, in
    // precedence order: this session's click, the in-session part text,
    // and the durable ExternalAction status (what survives a reload).
    const resolved = resolveOutcome({
      local: localResponse,
      partText: existingResponse,
      action: actionResponse,
    });
    // != null: user_response is optional on the payload type, so the chain
    // can yield undefined as well as null.
    if (resolved != null) {
      return (
        <Box data-testid="plan-publish-submitted-state" sx={{ pl: 2.75 }}>
          <Typography
            variant="caption"
            sx={{ color: grey[500], fontSize: "0.75rem" }}
          >
            ⎿&nbsp;&nbsp;
            {resolved.approved
              ? `Published to projects/${promptData.project_path}/`
              : "Declined"}
          </Typography>
        </Box>
      );
    }

    // Don't flash the buttons on a reloaded, already-resolved prompt while
    // the durable status is still being fetched.
    if (promptData.action_id && actionLoading) {
      return null;
    }

    // Without the host's stream store the outcome can't be written into the
    // dialogue or continued to the model, so a publish would be a
    // customer-visible write the conversation never learns about. The store
    // is momentarily null on a first turn while the host adopts the new
    // dialogueId — hold the buttons until it resolves.
    const canAct = streamStore !== null;

    // Pending: a single ⎿ child row in the compact tool-call style — no
    // card, no outline. Question + inline Decline/Publish actions.
    return (
      <Box
        data-testid="plan-publish-approval-container"
        sx={{
          pl: 2.75,
          pr: 0.5,
          py: 0.25,
          display: "flex",
          alignItems: "baseline",
          gap: 0.5,
          flexWrap: "wrap",
        }}
      >
        <Typography
          variant="caption"
          sx={{ color: grey[600], fontSize: "0.75rem" }}
          title="Everyone the project is shared with can discover this via Search Plans and run it via Checkout Plan."
        >
          ⎿&nbsp;&nbsp;Publish &ldquo;{promptData.template_name}&rdquo; to{" "}
          projects/{promptData.project_path}/
          {promptData.overwrite ? " (replaces existing)" : ""}?
        </Typography>
        {isSubmitting ? (
          <Typography
            variant="caption"
            sx={{ color: grey[500], fontSize: "0.75rem", ml: 0.5 }}
          >
            Publishing…
          </Typography>
        ) : !canAct ? (
          // No dialogueId yet = healthy first-turn adoption transient; a
          // key miss with an id is a real hold, so show the reason.
          dialogueId === undefined ? null : (
            <Typography
              variant="caption"
              data-testid="plan-publish-held-reason"
              sx={{ color: grey[500], fontSize: "0.75rem", ml: 0.5 }}
            >
              This view can't deliver approvals — open this chat from its main
              page.
            </Typography>
          )
        ) : (
          <>
            <Button
              size="small"
              onClick={handleApprove}
              data-testid="plan-publish-approve-button"
              data-attr="assistant:btn:approve-plan-publish"
              sx={{
                textTransform: "none",
                fontSize: "0.75rem",
                fontWeight: 600,
                minWidth: 0,
                px: 0.75,
                py: 0,
                lineHeight: 1.5,
              }}
            >
              Publish
            </Button>
            <Button
              size="small"
              onClick={handleReject}
              data-testid="plan-publish-cancel-button"
              data-attr="assistant:btn:reject-plan-publish"
              sx={{
                textTransform: "none",
                fontSize: "0.75rem",
                color: grey[500],
                minWidth: 0,
                px: 0.75,
                py: 0,
                lineHeight: 1.5,
              }}
            >
              Decline
            </Button>
          </>
        )}
        {error && (
          <Typography
            variant="caption"
            data-testid="plan-publish-error-alert"
            sx={{ color: red[600], fontSize: "0.75rem", width: "100%", pl: 3 }}
          >
            {error}
          </Typography>
        )}
      </Box>
    );
  },
);

PlanTemplatePublishApproval.displayName = "PlanTemplatePublishApproval";

export default PlanTemplatePublishApproval;
